/**
 * Single source of truth for backend API base URLs.
 *
 * The backend mounts every route under `/api/...` (see backend/src/server.js).
 * Two env vars are recognised so that historical code keeps working:
 *
 *   REACT_APP_API_BASE_URL  -> full base, INCLUDES the trailing /api
 *                              e.g. https://api.example.com/api
 *   REACT_APP_API_URL       -> host only, NO /api suffix
 *                              e.g. https://api.example.com
 *
 * If only one of them is configured we derive the other so call sites can
 * pick whichever shape they need without each one re-implementing the
 * "did the var include /api?" guess.
 */

const trimTrailingSlash = (value) => (value ? value.replace(/\/+$/, '') : value);

const RAW_BASE = trimTrailingSlash(process.env.REACT_APP_API_BASE_URL);
const RAW_HOST = trimTrailingSlash(process.env.REACT_APP_API_URL);

const DEFAULT_HOST = 'http://localhost:3001';
const DEFAULT_BASE = `${DEFAULT_HOST}/api`;

const deriveBase = () => {
  if (RAW_BASE) return RAW_BASE.endsWith('/api') ? RAW_BASE : `${RAW_BASE}/api`;
  if (RAW_HOST) return RAW_HOST.endsWith('/api') ? RAW_HOST : `${RAW_HOST}/api`;
  return DEFAULT_BASE;
};

const deriveHost = () => {
  if (RAW_HOST) return RAW_HOST.endsWith('/api') ? RAW_HOST.slice(0, -4) : RAW_HOST;
  if (RAW_BASE) return RAW_BASE.endsWith('/api') ? RAW_BASE.slice(0, -4) : RAW_BASE;
  return DEFAULT_HOST;
};

/** Full backend base, always includes `/api`. Use for `${API_BASE}/users/profile`. */
export const API_BASE = deriveBase();

/** Backend host without `/api`. Use for `${API_HOST}/api/inventory/items` legacy call sites. */
export const API_HOST = deriveHost();

/** Convenience: build a `${API_BASE}/<segment>` URL with no double-slashes. */
export const apiUrl = (path = '') => {
  if (!path) return API_BASE;
  return `${API_BASE}/${path.replace(/^\/+/, '')}`;
};
